/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import { type Capacity, type PromptConfigFull, CAPACITY_UNIT, Consts, I18nService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { StoragePoolAndLunWorkModeE } from 'app/common/constants';
import { DiskTypeAndEnumMap, DiskTypeE, DiskTypeOpts, EncryptDisks, MAX_ENG_NUM_PER_DISK_POOL_DEFAULT_VAL } from 'app/common/constants/disk-type';
import { ToolService } from 'app/common/services';
import { ProductConfig, ShardSwitch, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { type EngineInfo } from 'app/feature/system/storage-pool/model';
import { environment } from 'environments/environment';

import { type CapacityDisk, type EngineData, TierE } from './model';
import { DEFAULT_HOT_SPARE, DEFAULT_PERFORMANCE_DISK_NUM } from './storage-pool.const';

export const Constants = {
    HotSpareDisksMin: ProductConfig.DefaultSsdHotSpareDiskNum ?? DEFAULT_HOT_SPARE,
    HotSpareDisksMax: 8,
    EachEngineDiskMin: ((): number => {
        // 计算型存储支持4盘HDD扩容
        if (FeatureConfig.MinDiskNumOfEachEngine >= 0) {
            return FeatureConfig.MinDiskNumOfEachEngine;
        }

        // 支持X盘起配
        if (SWITCH.SUPPORT_X_DISK_CREATE_POOL) {
            return Number(SWITCH.SUPPORT_X_DISK_CREATE_POOL);
        }

        // 其他型号默认8盘
        return 8;
    }),
    /**
     * 性能层起配盘数 (2910为4, 5310和入门级存储为6)
     */
    PerFormanceEachEngineDiskMin: ProductConfig.MinSsdDiskNumberInDisklessDomain || DEFAULT_PERFORMANCE_DISK_NUM,

    // 有容量层硬盘，则性能层盘数最小是4盘
    MixedEachEngineDiskMin: 4,

    // x8000缓存盘最小起配盘
    OceanProtectCacheDiskMin: ShardSwitch.SHOW_FOUR_CACHE_DISK ? 4 : 6,
    /**
     * 框级冗余时，每个引擎最少要求的硬盘框数
     * release环境要求4框，debug时3框即可
     */
    EachEngineDiskEnclosureMin: environment.env === 'release' || environment.env === 'release_crack' ? 4 : 3,
    /**
     * 框级冗余时，每个硬盘框最少要求的硬盘数
     */
    EachDiskEnclosureDiskMin: 3,

    Str100Point00: '100.00',

    /** 4SSD + 4HDD + RAID-TP，最小非热备盘数量 */
    RAID_TP_MIN_DISK_NUM: 4,

    EachHotSpareEngineDiskMin: (): number => {
        if (FeatureConfig.MinNotHotSpareDiskNumOfEachEngine >= 0) {
            return FeatureConfig.MinNotHotSpareDiskNumOfEachEngine;
        }

        // RAID6需要2个校验列，至少1个数据列，所以非热备盘数至少是3
        if (Number(SWITCH.SUPPORT_X_DISK_CREATE_POOL) === 4) {
            return 3;
        }

        return 5;
    },
};

export enum RedundancyStrategyE {
    DiskLevel = '0',
    EnclosureLevel = '1'
}
// 加密算法 0：商密 AES，1：国密 SM4
export enum encryptAlgorithmE {
    Country = '0',
    Business = '1'
}

export const encryptAlgorithmList: SelectOption[] = [
    {
        value: '0',
        label: 'SM4',
    },
    {
        value: '1',
        label: 'AES',
    },
];

/**
 * 共享策略
 */
export enum ShareStrategy {
    // 共享策略
    Share = '0',
    // 独占策略
    Unique = '1'
}

export const NlDisks = {
    NL_SAS_SED: 11,
    NL_SAS: 4,
    'NL-SAS': 4,
};

// 数据销毁方式
export enum dataEraseE {
    OVERWRITE = '0',
    BLOCK_ERASE = '1',
    CRYPTOGRAPHIC_ERASE = '2'
}

// 硬盘是否加密
export enum encryptDiskTypeE {
    COMMON = '0',
    ENCRYPT = '1',
}

// 容量单位
export const CAPACITY_UNITS = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB', 'BB', 'NB', 'DB'];

// HDD类型盘
export const HddList = [DiskTypeE.SAS, DiskTypeE.SATA, DiskTypeE.NL_SAS, DiskTypeE.SAS_SED, DiskTypeE.SATA_SED, DiskTypeE.NL_SAS_SED];

// HDD普通盘
export const HddCommonDisks = [DiskTypeE.SAS, DiskTypeE.SATA, DiskTypeE.NL_SAS];

/**
 * 最大RAID列数
 */
export const raidMemberNumValue = {
    MIN: 4,
    MAX: 25,
    // RAID10默认设置为4
    RAID_10: 4,
    // RAID6和RAID-TP默认设置为25
    COMMON_VALUE: 25,
};

// 预检查需要提示到界面的错误码【1107364360：错误场景：对NAS迁移任务执行过程中的硬盘域执行部署控制器变更操作。】
export const ExistNasMoveErrCode = 1107364360;

@Injectable()
export class StoragePoolStrService {
    get = this.i18nService.get;
    isEn = this.i18nService.language === 'en';

    Constants = Constants;
    FeatureConfig = FeatureConfig;

    // 创建或者扩容的存储池物理接入容量不能超过0.75PB
    maxCapacity = this.utilService.adaptUnit(0.75, 'PB', 'Sector').value;

    /**
     * 定制显示的硬盘类型，比如底层返回Capacity-Optimized SSD类型硬盘，需要显示成 容量型SSD
     */
    showDiskTypeByI18nDisks = {
        'Capacity-Optimized SSD': this.get('system.EnumDefine_DISK_TYPE_E_CapacityOptimized_SSD'),
        'Capacity-Optimized SSD SED': this.get('system.EnumDefine_DISK_TYPE_E_CapacityOptimized_SSD_SED'),
        'Phony Capacity-Optimized SSD': this.get('system.EnumDefine_DISK_TYPE_E_CapacityOptimized_SSD'),
        'Phony Capacity-Optimized SSD SED': this.get('system.EnumDefine_DISK_TYPE_E_CapacityOptimized_SSD_SED'),
    };

    commonI18n = {
        // 多dd
        capacity: this.get('common.capacity_column'),
        singleCapacity: this.get('storage.storagePool_task_create_singleDiskCap_cloumn'),
        diskType: this.get('common.type_column'),
        totalDiskNum: this.get('storage.storagePool_task_create_totalDisk_cloumn'),
        availableDiskNumLab1: this.get('storage.storagePool_task_create_controlEncloCommonDiskNum_cloumn'),
        availableDiskNumLab2: this.get('storage.storagePool_task_create_diksEncloCommonDiskNum_cloumn'),
        availableDiskNumTip1: this.get('storage.storagePool_task_create_controlEncloCommonDiskNum_tip'),
        availableDiskNumTip2: this.get('storage.storagePool_task_create_diksEncloCommonDiskNum_tip'),
        requireDiskNum: this.get('storage.storagePool_task_create_number_needDisk_cloumn'),
        selectedDiskNumLab: this.get('storage.storagePool_create_task_selectedDiskNum_lab'),
        memberDiskNumLab: this.get('storage.storagePool_create_task_memberDiskNum_lab'),
        selectedDisksCloumn: this.get('storage.storagePool_task_create_selectedDisks_cloumn'),

        // select-disk
        selectDiskTitle: this.get('storage.storagePool_task_expand_selectDisk_title'),
        selectedDisk: this.get('storage.storagePool_task_expand_selectDisk_selected_lab'),
        availableCap: this.get('common.availableCapacity_lab'),
        storageCap: this.get('storage.lun_task_create_storagePoolCapacity_lab'),
        redundancyStrategyLab: this.get('storage.storagePool_task_create_reduPolicy_lab'),
        performanceLayerShareStrategyLab: this.get('storage.storagePool_task_create_performTierSharePolicy_lab'),
        performanceLayerShareStrategyTip: this.get('storage.storagePool_task_create_performTierSharePolicy_tip'),
        shareStrategy: this.get('storage.storagePool_task_create_performTierSharePolicy_shared_value'),
        uniqueStrategy: this.get('storage.storagePool_task_create_performTierSharePolicy_exclusive_value'),
        encryptAlgorithmLab: this.get('settings.snmpProtocol_term_addUser_encryptProtocol_label'),
        sm4AlgorithmLab: this.get('storage.storagePool_task_create_encryptAlgorithm_SM4_listbox'),
        aesAlgorithmLab: this.get('settings.snmpProtocol_term_addUser_encryptProtocol_AES_listbox'),
        redundancyAvaliablityLab: this.get('storage.storagePool_task_create_reduAbility_lab'),
        diskEnclosureLab: this.get('common.diskEnclousre_lab'),
        engineAndDiskEnclosureLab: this.get('storage.storagePool_task_create_controlAndDiskEnclo_lab'),
        selectEngineAndDiskEnclosureTitle: this.get('storage.storagePool_task_create_controlAndDiskEnclo_title'),
        unselectedDiskEnclosureLab: this.get('storage.storagePool_task_create_availDiskEnclo_lab'),
        selectedDiskEnclosureLab: this.get('storage.storagePool_task_create_selectedDiskEnclo_lab'),
        smartTierLab: this.get('storage.storagePool_task_create_multiTiers_lab'),
        smartTierTip: this.get('storage.storagePool_task_create_multiTiers_tip'),
        smartTierSoftEncryptTip: this.get('storage.storagePool_task_create_multiTiers_softEncrypt_tip'),
        tierLab: this.get('storage.storagePool_task_create_tier_column'),
        blockReductionRatioLab: this.get('storage.dashboad_term_blockDataCompressionRate_label'),
        fileReductionRatioLab: this.get('storage.dashboad_term_fileDataCompressionRate_label'),
        blockReductionRatioTip: this.get('storage.dashboard_capacity_blockDataRatio_tip'),
        fileReductionRatioTip: this.get('storage.dashboard_capacity_fileDataRatio_tip'),
        algorithmHelpContent: this.get('storage.storagePool_task_create_encryptProtocol_tip'),

        currentCap: this.get('common.currentCapacity_label'),
        capacityLabel: this.get('storage.lun_task_expand_capacity_label'),
        currentEngineLab: this.get('storage.storagePool_task_expand_currentController_lab'),
        expandedCap: this.get('storage.lun_task_expand_capacityAfterExapnded_label'),
        used: this.get('storage.storagePool_expand_task_used_label'),

        // 硬盘数量应保证每个控制框的硬盘数量相同。
        engineNotSameMsg: this.get('storage.storagePool_task_create_number_needDisk_multipleController_tip'),
        // 新增控制框的硬盘数量必须与原控制框相同。
        engineAddMsg: this.get('storage.storagePool_task_expand_equalType_betweenController_tip'),
        // 硬盘类型必须相同
        diskTypeNotSameMsg: this.get('storage.storagePool_task_expand_selectDiskType_toController_tip'),
        // 每种硬盘类型最多选择两种容量
        diskCapLengthMsg: this.get('storage.storagePool_task_create_diskCap_err_lab'),
        // 您已选择硬盘，是否继续输入硬盘数量操作？<br/>该操作将重置所有选择的硬盘。
        diskChangeNumMsg: this.get('storage.storagePool_create_task_switchToInput_info_label'),
        // 您已输入硬盘数量，是否继续选择硬盘操作？<br/>该操作将重置所有输入的硬盘数量。
        diskChangeSelectMsg: this.get('storage.storagePool_create_task_switchToSet_info_label'),
        // 您已选择硬盘，是否重新选择硬盘？
        diskSelectTypeChangeMsg: this.get('storage.storagePool_create_task_selectedDisk_confirm_info'),
        // 需要硬盘数量不能大于可用硬盘数量
        diskGtMaxMsg: this.get('storage.storagePool_create_task_availableLarger_info_label'),
        expandDiskMaxErr: this.get('storage.storagePool_task_expand_controlEnclo_needDisk_error_lab'),
        expandEnclosureLevelMaxErr: this.get('storage.storagePool_task_expand_number_needDisk_error_lab'),
        creatDiskMaxErr: this.get('storage.storagePool_task_create_controlEnclo_needDisk_error_lab'),
        creatEnclosureLevelMaxErr: this.get('storage.storagePool_task_expand_number_needDisk_error_lab'),
        // 控制框不能为空。
        engineEmptyMsg: this.get('common.null_validation', [this.get('storage.storagePool_task_create_controller_lab')]),
        // 控制框和硬盘框不能为空
        engineAndDiskEnclosureEmptyMsg: this.get('common.null_validation', [
            this.get('storage.storagePool_task_create_controlAndDiskEnclo_lab'),
        ]),
        // 控制框上限，直接从ProductConfig读取
        engineMaxErr: this.get('storage.storagePool_task_create_controller_dorado_info_lab', [ProductConfig.MaxEngNumPerDiskPool ?? MAX_ENG_NUM_PER_DISK_POOL_DEFAULT_VAL]),
        // 扩容时选择的硬盘容量不能小于已有的最大的硬盘容量。（支持特殊场景：存储池有两种容量时，可以扩容这两种容量）
        diskMinCapMsg: this.get('storage.storagePool_expand_task_selectCapacity_info_label'),
        smartTierDiskMinCapErr: this.get('storage.storagePool_task_expand_enableSmartTier_selectCapacity_info'),
        // 选择的硬盘数量太少不满足系统规格。
        diskNotEnoughCapMsg: this.get('storage.storagePool_task_expand_selectedNumInvaild_info_lab'),
        // SmartTier类型校验错误
        smartTierDiskTypeErr: this.get('storage.storagePool_task_create_enableSmartTier_diskType_info'),
        tierPerTypeErr: this.get('storage.storagePool_task_create_enableSmartTier_diskType_info'),
        expandDiskMinErr: this.get('storage.storagePool_expand_task_selectOne_info'),
        selectByIdErr: this.get('storage.storagePool_expand_task_manualSelect_err_info'),

        hotSpareDisks: this.get('storage.storagePool_term_create_hotSpareDiskNum_lab'),
        hotSpareDisksTip: this.get('storage.storagePool_term_create_hotSpareDiskNum_tip'),
        hostSpareDisksCreateMsg: this.get('storage.storagePool_create_task_nullHotSpare_fail_msg'),
        hostSpareDisksModifyMsg: this.get('storage.storagePool_modify_task_reduceHotSpace_msg'),
        // 加密盘创建非加密存储池高危提示
        useEncryptedDiskMsg: this.get('storage.storagePool_create_task_useEncryptedDisk_confirm_msg'),

        diskMustEqualInEveryEngine: this.get('storage.storagePool_task_create_controlEncloSelectDisk_error_info_lab'),
        needDiskOnCreateTip: this.get('storage.storagePool_task_create_number_needDisk_tip'),
        needDiskOnExpandTip: this.get('storage.storagePool_task_expand_number_needDisk_tip'),
        diskLevelNeedDiskOnCreateTip: this.get('storage.storagePool_task_create_controlEnclo_needDisk_tip'),
        diskLevelNeedDiskOnExpandTip: this.get('storage.storagePool_task_expand_controlEnclo_needDisk_tip'),
        requiredDiskErr: this.get('common.null_validation', [this.get('storage.storagePool_task_create_number_needDisk_cloumn')]),
        diskMustEqualInEveryEnslocureTip: this.get('storage.storagePool_task_create_selectDisk_info_lab', [
            Constants.EachDiskEnclosureDiskMin,
        ]),
        diskMustEqualInEveryEnslocureErr: this.get('storage.storagePool_task_create_selectDiskSame_error_info_lab', [
            Constants.EachDiskEnclosureDiskMin,
        ]),
        diskInvalidTip: this.get('storage.storagePool_task_expand_needDiskNumInvaild_info'),
        allflashPoolFirstInfo: this.i18nService.get('storage.storagePool_task_create_allflashPoolFirst_info'),

        // 导出配置
        exportBtn: this.get('storage.storagePool_create_task_importDiskConfiguration_button'),
        exportTitle: this.get('storage.storagePool_create_task_importDiskConfiguration_title'),
        // exportInfo: this.get('通过该功能您可以在实施扩容前查询并导出硬盘配置信息，包括硬盘的容量和数量。'),
        id: this.get('common.ID_column'),
        colonLab: this.get('common.colon_label'),
        selectBtn: this.get('storage.storagePool_task_create_requiredDisk_select_button'),
        modifyBtn: this.get('common.modify_button'),

        controllerEnclosure: this.get('storage.storagePool_task_create_controller_lab'),
        deletePoolTitle: this.get('storage.storagePool_delete_task_title_wintitle'),
        cacheDisk_lab: this.get('storage.storagePool_create_task_cacheDisk_lab'),
        dataDisk_lab: this.get('storage.storagePool_create_task_dataDisk_lab'),
        selectedDiskNum_lab: this.get('storage.storagePool_create_task_selectedDiskNum_lab'),
        diskRedu_value: this.get('storage.storagePool_task_create_diskRedu_value'),
        diskTotalNum_lab: this.get('storage.storagePool_expand_task_diskTotalNum_lab'),
        compressLevelLab: this.get('storage.storagePool_create_task_compressLevel_lab'),
        overLimit: this.get('storage.storagePool_create_task_overLimit_confirm_msg'),

        // 闲时回收时间
        confirmBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),

        workday: this.get('storage.storagePool_task_recycleIDTable_freeTime_workDay_value'),
        weekend: this.get('storage.storagePool_task_recycleIDTable_freeTime_weekend_value'),
        allDay: this.get('storage.storagePool_task_recycleIDTable_freeTime_everyDay_value'),
        idleMode: this.get('storage.storagePool_task_recycleIDTable_freeTime_lab'),
        startTime: this.get('common.startTime_label'),
        endTime: this.get('common.endTime_label'),
        idleHeaderLab: this.get('storage.storagePool_task_recycleIDTable_config_lab'),
        configRecycleLab: this.get('rest.configRecycleIDTable_msg'),
        oneClickLab: this.get('storage.storagePool_task_recycleIDTable_click_lab'),
        canModifyConfigLab: this.get('storage.storagePool_task_recycleIDTable_modifyPolicy_lab'),
        startBtn: this.get('storage.storagePool_task_recycleIDTable_execute_btn'),
        endBtn: this.get('storage.storagePool_task_recycleIDTable_stop_btn'),
        startMsg: this.get('storage.storagePool_task_recycleIDTable_execute_confirm_msg'),
        endMsg: this.get('storage.storagePool_task_recycleIDTable_stop_confirm_msg'),
        modifyStrategyMsg: this.get('storage.storagePool_task_recycleIDTable_config_confirm_msg'),
        performanceLayersTab: this.get('storage.storagePool_create_task_performanceLayers_tab'),
        StoragePoolsLab: this.get('common.StoragePools'),
        owningControllerLab: this.get('storage.storagePool_view_task_owningController_lab'),
        selectedDisksLab: this.get('storage.storagePool_view_task_selectedDisks_lab'),
        perforLayerManualConfigInfo: this.get('storage.storagePool_term_create_perforLayerManualConfig_info'),
        perforLayerShareStrategyInfo: this.get('storage.storagePool_term_create_perforLayerManualConfig_info2'),
        perforLayerRedundancyStrategyInfo: this.get('storage.storagePool_term_create_perforLayerManualConfig_info3'),
        diskOfPoolAndPerfomanceTip: this.get('storage.storagePool_task_create_selectDiskEnclo_info'),
        capHotSpareDiskNumTip: this.get('storage.storagePool_term_create_capHotSpareDiskNum_tip'),
        capHotSpareDiskNumCommonTip: (count: number): string => this.get('storage.storagePool_term_create_capHotSpareDiskNum_common_tip', [count]),
        capacityTierRequirDiskInfo: this.get('compute.storagepool_create_capacityTierRequirDisk_info'),
        performHotSpareDiskNumTip: this.get('storage.storagePool_term_create_performHotSpareDiskNum_tip'),
        capSelectCapacityInfoLabel: this.get('storage.storagePool_expand_task_capSelectCapacity_info_label'),
        performSelectCapacityInfoLabel: this.get('storage.storagePool_expand_task_performSelectCapacity_info_label'),
        performHotSpareDiskTip: this.get('storage.storagePool_term_create_performHotSpareDisk_tip'),
        performHotSpareDiskAATip: this.get('storage.storagePool_term_create_performHotSpareDisk_AA_tip'),
        performHotSpareDiskNumAATip: this.get('storage.storagePool_term_create_performHotSpareDiskNum_AA_tip'),
        encryptedAndCommonDiskInfo: this.get('storage.storagePool_term_create_encryptedAndCommonDisk_info'),
        performDisksMsg: this.get('rest.getPerformDisks_msg'),
        createRecommendConfigMsg: this.get('storage.storagePool_create_task_recommendConfig_msg'),
        expandrecommendConfigMsg: this.get('storage.storagePool_expand_task_recommendConfig_msg'),
        sasAndNlsasDiskInfo: this.get('storage.storagePool_term_create_SASAndNLSASDisk_info'),
        availabledDisksTip: this.get('storage.storagePool_task_expand_availableDisk_tip'),
        totalCapacityTip: this.get('storage.storagePool_view_task_totalCapacity_tip'),
        //同时选择正真假容量型硬盘提示
        differentCapacityOptimizedDiskInfo: this.get('storage.storagePool_task_create_sameBom_info'),
        performanceLayerRedundancyStrategyLab: this.get('storage.storagePool_task_create_performTierRedundancyPolicy_lab'),
    };

    hotSpareStrategy = {
        default: this.i18nService.get('common.default_label'),
        none: this.i18nService.get('home.InitialConfig_StoragePool_HotSpareDisk_None_value'),
        low: this.i18nService.get('home.InitialConfig_StoragePool_HotSpareDisk_Low_value'),
        high: this.i18nService.get('home.InitialConfig_StoragePool_HotSpareDisk_High_value'),
        customize: (count: number | string): string => this.i18nService.get('home.InitialConfig_StoragePool_HotSpareDisk_custom_value', [Number(count) - 4]),
    };

    diskTypeOpts = this.toolService.convertEnumToOptions(DiskTypeOpts);

    deleteI18n = {
        eraseData: this.i18nService.get('system.systemSetting_setEncryptDrive_term_dataErasure_button'),
        eraseTip: this.i18nService.get('storage.storagePool_setEncryptDrive_term_dataErasure_confirm_tip'),
        normalHighRiskTip: (name: string): string => this.i18nService.get('storage.storagePool_task_deletePool_FS_confirm_msg', [name]),
        eraseHighRiskTip: this.i18nService.get('storage.storagePool_delete_task_storagePoolAndDiskDomain_noData_confirm_msg'),
        verifyTips: this.i18nService.get('storage.diskDomain_task_delete_ErasureAndVerify_msg'),
        performLayerVerifyTips: this.i18nService.get('storage.storagePool_delete_task_enableDataErasure_confirm_msg'),
    };

    createI18nTip = {
        withoutKeyServiceTip: this.i18nService.get('storage.diskdomain_task_create_SelfencryptDisk_encryptionService_message'),
        withoutKeyServiceTip1: this.i18nService.get('storage.diskdomain_task_create_withSelfencryptDisk_encryptionService_tip_1'),
        withoutKeyServiceTip2: this.i18nService.get('storage.diskdomain_task_create_withSelfencryptDisk_encryptionService_tip_2'),
        withoutKeyServiceTip3: this.i18nService.get('storage.diskdomain_task_create_withSelfencryptDisk_encryptionService_tip_3'),
        withKeyServiceTip1: this.i18nService.get('storage.diskdomain_task_create_withSelfencryptDisk_keyService_confirm_message_1'),
        withKeyServiceTip2: this.i18nService.get('storage.diskdomain_task_create_withSelfencryptDisk_keyService_confirm_message_2'),
        withKeyServiceTip3: this.i18nService.get('storage.diskdomain_task_create_withSelfencryptDisk_encryptionService_tip_3'),
        oneEngineDiskNotEnoughTip: this.i18nService.get('storage.storagePool_task_create_selectDiskNumber_msg'),
        twoEngineDiskNotEnoughTip: this.i18nService.get('storage.storagePool_task_create_selectDoubleDiskNumber_msg'),
        diskCapDiffCountTip: this.i18nService.get('storage.storagePool_createOrExpend_task_diffrentDiskCap_tip'),
        noEncryptDiskTip: this.i18nService.get('storage.diskpool_create_task_info_noCorrelatedEncryptDisk_msg'),
        noDiskTip: this.i18nService.get('storage.storagePool_expand_task_noValidController_msg'),
        maxQuotaTip: this.i18nService.get('storage.storagePool_task_create_performTierQuota_tip'),
        maxRaidMemberNumTip: this.i18nService.get('storage.storagePool_term_maxRaid_info'),
        maxRaidMemberNumPrompt: this.i18nService.get('storage.storagePool_term_maxRaid_comfirm_msg'),
        workModeHelpTip: this.i18nService.get('storage.storagePool_term_workMode_tip'),
        usedUpAlarmThresholdLabTip: this.i18nService.get('storage.storgePool_modify_task_capacityUsedUp_tip'),
        setKeyServiceLab: this.i18nService.get('storage.storagePool_create_task_dataEncrypt_configKeyService_tip'),
        pmsTip: this.i18nService.get('settings.monitorSetting_term_paraSetting_dataStorage_info_label'),
        queryProgressTimeoutTip: this.i18nService.get('common.task_progressTimeout_tip'),
        consumedCapacityThresholdCompareNumErr: this.i18nService.get('storage.storgePool_create_task_cpaAlarmTeshold_value_tip'),
        protectCapacityThresholdCompareNumErr: this.i18nService.get('storage.storagePool_create_task_protectCapacity_compare_tip'),
        requiredErr: this.i18nService.get('common.null_validation', [this.i18nService.get('common.name_column')]),
        quotaInsuffErr: this.i18nService.get('storage.storagePool_task_create_performTierQuota_desc'),
        encryptionDisableTip: this.get('storage.storagePool_create_dataEncryption_err_tip'),
        minPerformanceHotSpareTip: this.get('storage.storagePool_term_create_performHotSpareDisk_atLeastOne_info'),
        createApPoolTip: this.get('storage.storagePool_task_createPool_ap_confirm_msg'),
    };

    /**
     * 冗余策略选项列表
     */
    redundancyStrategyOpts: SelectOption[] = [
        {
            label: this.get('storage.storagePool_task_create_diskRedu_value'),
            value: RedundancyStrategyE.DiskLevel,
        },
        ...(
            // 只有Dorado涉及框级冗余，Dorado低端型号、二级存储、微存储、新融合不支持框级冗余
            (!FeatureConfig.IsSupportEnclosureLevel) ? [] : [{
                label: this.get('storage.storagePool_task_create_encloRedu_value'),
                value: RedundancyStrategyE.EnclosureLevel,
            }]
        ),
    ];

    encryptAlgorithmOpts: SelectOption[] = [
        {
            label: this.commonI18n.sm4AlgorithmLab,
            value: encryptAlgorithmE.Country,
        },
        {
            label: this.commonI18n.aesAlgorithmLab,
            value: encryptAlgorithmE.Business,
        },
    ];
    /**
     * 压缩级别选项列表
     */
    compressLevelOpts: SelectOption[] = [
        {
            label: this.get('storage.storagePool_create_task_compressLevel_performance_value'),
            value: '0',
        },
        {
            label: this.get('storage.storagePool_create_task_compressLevel_reduction_value'),
            value: '1',
        },
    ];

    /**
     * 冗余能力选项列表
     */
    redundancyAbilityOpts: SelectOption[] = [
        ...this.redundancyStrategyOpts,
        {
            label: this.get('storage.storagePool_task_create_noneRedu_value'),
            value: '2',
        },
    ];

    tierI18nMap = {
        [TierE.Performance]: this.get('storage.storagePool_task_create_performTier_value'),
        [TierE.Capacity]: this.get('storage.storagePool_task_create_capTier_value'),
    };

    // 性能层共享策略
    shareStrategyMap = {
        [ShareStrategy.Share]: this.commonI18n.shareStrategy,
        [ShareStrategy.Unique]: this.commonI18n.uniqueStrategy,
    };

    // 可能有重名的，其他页面还是用单独的
    i18n = {
        ...this.createI18n,
        ...this.detailI18n,
    };

    /**
     * 存储池工作模式选项
     */
    workModeOpts: SelectOption[] = [
        {
            label: this.i18n.aaModeLab,
            value: StoragePoolAndLunWorkModeE.AA_MODE,
        },
        {
            label: this.i18n.apModeLab,
            value: StoragePoolAndLunWorkModeE.AP_MODE,
        },
    ];

    get createI18n(): { [key: string]: string | any | any } {
        const i18nService = this.i18nService;
        return {
            ...this.commonI18n,
            ...this.createI18nTip,
            winTitle: i18nService.get('storage.storagePool_create_task_title_wintitle'),
            advancedLab: i18nService.get('common.advance_label'),
            nameLab: i18nService.get('common.name_column'),
            descriptionLab: i18nService.get('common.description_column'),
            dataEncryptionLab: i18nService.get('settings.snmpProtocol_task_addUser_dataEncrypt_checkbox'),
            allDiskLab: i18nService.get('storage.diskPool_term_diskType_value_allDisk_value'),
            selectDiskLink: i18nService.get('storage.storagePool_task_create_requiredDisk_select_button'),
            raidLevelLab: i18nService.get('storage.storagePool_term_raidStrategy_label'),
            raidMemberNumber: i18nService.get('storage.storagePool_term_maxRaid_label'),
            alarmThresholdLab: i18nService.get('storage.storagePool_create_task_para_consumedCapThreshold_label'),
            workModeLab: i18nService.get('storage.storagePool_term_affinityMode_label'),
            aaModeLab: i18nService.get('storage.storagePool_term_workMode_aa_value'),
            apModeLab: i18nService.get('storage.storagePool_term_workMode_ap_value'),
            usedUpAlarmThresholdLab: i18nService.get('storage.storgePool_create_task_seniorsetting_para_tobe_exhausted_capacity_percent_label'),
            enableThinLunOverLab: i18nService.get('storage.storgePool_create_task_seniorsetting_para_thinLUNProportion_label'),
            overPercentageLab: i18nService.get('storage.storgePool_create_task_seniorsetting_para_thinLUNProportion_percent_label'),
            autoDeleteSwitchLab: i18nService.get('storage.storagePool_create_task_para_autoDelete_noUnit_label'),
            protectionCapacityLowLimitLab: i18nService.get('storage.storagePool_create_task_para_protectCapacityLowThreshold_label'),
            protectionCapacityHighLimitLab: i18nService.get('storage.storagePool_create_task_para_protectCapacitUpThresholdy_label'),
            pmsSwitchLab: i18nService.get('settings.monitorSetting_term_saveMonitorData_checkbox'),

            openAutoDeleteTitle: i18nService.get('storage.storagePool_modify_task_openAutoDelete_title'),
            closeAutoDeleteTitle: i18nService.get('storage.storagePool_modify_task_closeAutoDelete_title'),
            openAutoDeleteMsg: i18nService.get('storage.storagePool_modify_task_openAutoDelete_msg'),
            closeAutoDeleteMsg: i18nService.get('storage.storagePool_modify_task_closeAutoDelete_msg'),

            confirmBtn: i18nService.get('common.confirm_button'),
            cancelBtn: i18nService.get('common.cancel_button'),
            closeBtn: i18nService.get('common.close_button'),

            taskCreationResult: i18nService.get('storage.storagePool_task_result_value1'),
            queryProgressResult: i18nService.get('common.task_getProgress_lab'),
            dataDiskValid_info: i18nService.get('storage.storagePool_create_task_dataDiskValid_info'),
            basicInfoLab: this.get('common.basicInfo_title'),
            capacityLayerCapLab: this.get('storage.storagePool_create_task_capacityLayer_lab'),
            capacityLayerLab: this.get('storage.storagePool_create_task_capacityLayer_lab'),
            performanceLayerLab: this.get('storage.storagePool_create_task_performanceLayer_lab'),
            capacityLayerControlInfo: this.get('storage.storagePool_create_task_capacityLayerControl_info'),
            capacityLayerCapNumValid: this.get('storage.storagePool_create_task_capacityLayerCapNum_valid'),
            performanceLayerCapNumValid: this.get('storage.storagePool_create_task_performanceLayerCapNum_valid'),
            viewPerforDisksLab: this.get('storage.storagePool_create_task_viewPerforDisks_lab'),
            storagePoolTitle: this.get('common.storagePool_title'),
            storagePoolHeader: this.get('monitor.charts_term_objectType_STORAGEPOOL'),
            createAvailCapInfo: this.get('storage.storagePool_term_create_availCap_info'),
            expandPerformanceLayer: this.get('storage.storagePool_term_create_expandPerformanceLayer_checkbox'),
            capTierHotSpareDiskLab: this.get('storage.storagePool_term_create_capTierHotSpareDisk_lab'),
            allocateCapCheckbox: this.get('storage.storagePool_term_expand_allocateCap_checkbox'),
            availCapacityDeficientInfo: this.get('storage.storagePool_term_availCapacityDeficient_info'),
            performanceLayerQuotaLab: this.get('storage.storagePool_view_task_performanceLayerQuota_lab'),
            selectedFreeDiskNumLab: this.get('storage.storagePool_create_task_selectedFreeDiskNum_lab'),
            storagePoolName: this.get('monitor.charts_term_objectType_STORAGEPOOL'),
            performHotSpareDiskLab: this.get('storage.storagePool_term_create_performHotSpareDisk_lab'),
            defaultLabel: this.get('common.default_label'),
        };
    }

    get detailI18n(): { [key: string]: string | any } {
        const i18nService = this.i18nService;
        return {
            ...this.commonI18n,
            createBtn: i18nService.get('common.create_button'),
            expandBtn: i18nService.get('common.expand_label'),
            rekeyBtn: i18nService.get('storage.storagePool_updatekey_button'),
            modifyBtn: i18nService.get('common.modify_button'),
            deleteBtn: i18nService.get('common.delete_button'),
            actionBtn: i18nService.get('common.operation_button'),

            summaryLab: i18nService.get('common.task_collectInfo_title_label'),
            disksLab: i18nService.get('common.Disks'),

            healthStatusLab: i18nService.get('common.healthStatus_column'),
            runningStatusLab: i18nService.get('common.runningStatus_column'),
            dataEncryptionLab: i18nService.get('settings.snmpProtocol_task_addUser_dataEncrypt_checkbox'),
            raidLevelLab: i18nService.get('storage.storagePool_term_raidStrategy_label'),
            yesLab: i18nService.get('common.term_yes_label'),
            noLab: i18nService.get('common.term_No_label'),
            descriptionLab: i18nService.get('common.description_column'),
            ...this.capacityI18n,

            // 订阅容量
            blockLab: this.get('common.block_listbox'),
            fsLab: this.get('common.file_listbox'),

            basicInfoLab: this.get('common.basicInfo_title'),
            efficiencyLab: i18nService.get('storage.storagePool_view_task_summary_efficiency_label'),
            overallSpaceSavingRatioTip: i18nService.get('storage.dashboard_capacity_Total_Saving_Ratio_tip'),
            dataReductionRatioTip: i18nService.get('storage.dashboard_capacity_DataRatio_tip'),
            hotSpareDiskNumInfo: i18nService.get('storage.storagePool_term_create_hotSpareDiskNum_info'),
            dataDeduplicationrateRatioTip: i18nService.get('storage.dashboad_term_dataDeduplicationRate_tip'),
            blockDeduplicationrateRatioTip: i18nService.get('storage.dashboad_term_blockDataDeduplicationRate_tip'),
            fileDeduplicationrateRatioTip: i18nService.get('storage.dashboad_term_FSDataDeduplicationRate_tip'),
            dataCompressionrateRatioTip: i18nService.get('storage.dashboad_term_datacompressRate_tip'),
            blockCompressionrateRatioTip: i18nService.get('storage.dashboad_term_blockDatacompressRate_tip'),
            fileCompressionrateRatioTip: i18nService.get('storage.dashboad_term_FSDatacompressRate_tip'),
            thinLunSpaceSavingRateTip: i18nService.get('storage.dashboard_capacity_ThinLUN_Saving_Ratio_tip'),

            performanceLab: i18nService.get('common.dashboard_perf_static_title'),
            latencyLab: i18nService.get('storage.storagePool_view_task_avgbackendResponseTime_label'),
            iopsLab: i18nService.get('storage.smartQoS_term_IOPS_label'),
            bandWidthLab: i18nService.get('home.performance_term_blockBandwidth_title'),

            diskDomainNameLab: i18nService.get('storage.storagePool_term_diskDomainName_lab'),
            diskTypeLab: i18nService.get('storage.storagePool_term_medium_column'),
            unbalanceDiskIdLab: i18nService.get('storage.storgePool_view_task_para_AntiWearLevelingDiksID_label'),
            avgAbrasionRateLab: i18nService.get('storage.storagePool_view_task_disk_avgLifeSpan_label'),
            avgRemainLifeLab: i18nService.get('storage.storagePool_view_task_disk_avgPredictedLife'),
            monthLab: i18nService.get('common.lower_months_label'),
            monthLabLow: i18nService.get('common.time_months_label'),
            hotSpareCapacityLab: i18nService.get('storage.diskpool_term_StandbyCap'),

            diskListLab: i18nService.get('storage.storagePool_term_diskList_lab'),
            locationLab: i18nService.get('common.location_column'),
            typeLab: i18nService.get('common.type_column'),
            remainLifeLab: i18nService.get('storage.device_term_LifeSpanForMonth_label'),

            closeBtn: i18nService.get('common.close_button'),
            rekeyMsg: i18nService.get('storage.storagePool_task_updateKey_tip'),
            notEncryptDiskTip: i18nService.get('storage.storagePool_updatekey_tip'),
            capNotZeroTip: i18nService.get('storage.storagePool_delete_tip'),
            quotaLab: i18nService.get('common.quota_lab'),
            cacheUsedCapLab: i18nService.get('storage.storagePool_view_task_cacheUsedCap_tip'),
            dataUsedCapLab: i18nService.get('storage.storagePool_view_task_dataUsedCap_tip'),
            performanceLayerHitRatioLab: i18nService.get('storage.storagePool_view_task_performanceLayerHitRatio_lab'),
            more_button: i18nService.get('common.more_button'),
            maxCapacityLimitLab: this.get('storage.storagePool_view_task_quotaUPlimit_lab'),
        };
    }

    /**
     * 容量相关i18n
     */
    get capacityI18n(): { [key: string]: string | any } {
        const i18nService = this.i18nService;
        return {
            capacityLab: i18nService.get('common.capacity_column'),
            manuCapacity: i18nService.get('system.hardware_term_ManufaturCap_column'),
            availableCapacityLab: i18nService.get('storage.storagePool_term_freeCapacity_label'),
            availableCapacityTip: i18nService.get('storage.storagePool_view_task_availableCapacity_tip'),
            overallSpaceSavingRatioLab: i18nService.get('storage.storgePool_view_term_whole_space_percentage_label'),
            dataReductionRatioLab: i18nService.get('storage.dashboad_term_dataCompressionRate_label'),
            dataDeduplicationrateRatioLab: this.get('storage.dashboad_term_dataDeduplicationRate_label'),
            blockDeduplicationrateRatioLab: this.get('storage.dashboad_term_blockDataDeduplicationRate_label'),
            fileDeduplicationrateRatioLab: this.get('storage.dashboad_term_FSDataDeduplicationRate_label'),
            dataCompressionrateRatioLab: this.get('storage.dashboad_term_datacompressRate_label'),
            blockCompressionrateRatioLab: this.get('storage.dashboad_term_blockDatacompressRate_label'),
            fileCompressionrateRatioLab: this.get('storage.dashboad_term_FSDatacompressRate_label'),
            thinLunSpaceSavingRateLab: i18nService.get('storage.storgePool_view_term_thinLUN_space_percentage_label'),
            totalCapacityLab: i18nService.get('common.totalCapacity_column'),
            totalLab: i18nService.get('common.totalCapacity_lab'),
            diskCapacityLab: i18nService.get('storage.storagePool_term_diskCapacity_label'),
            usedCapacityLab: i18nService.get('storage.dashboard_term_CapacityInfo_UsedRawCapacity_label'),
            unusedCapacityLab: i18nService.get('storage.dashboard_term_CapacityInfo_UnusedRawCapacity_label'),
            subscripedCapacityLab: i18nService.get('storage.storagePool_view_task_subscribed_lab'),
            subscripedCapacityTip: i18nService.get('storage.storagePool_view_task_subscribed_tip'),
            subscripedCapNoFsTip: i18nService.get('storage.storagePool_view_task_subscribed_noFS_tip'),
            subscripedCapNoSanTip: i18nService.get('storage.storagePool_view_task_subscribed_D2100_tip'),
            dataProtectionCapacityLab: i18nService.get('storage.storagePool_term_dataProtectionCapacity_column'),
            dataProtectionCapacityTip: i18nService.get('storage.storagePool_term_dataProtection_column'),
            alarmThresholdLab: i18nService.get('storage.storagePool_create_task_para_consumedCapThreshold_lab1'),
            usedUpAlarmThresholdLab: i18nService.get('storage.storagePool_create_task_tobe_exhausted_capacity_lab'),
            protectionCapacityLowLimitLab: i18nService.get('storage.storagePool_create_task_para_protectCapacityLowThreshold_lab1'),
            protectionCapacityHighLimitLab: i18nService.get('storage.storagePool_create_task_para_protectCapacitUpThresholdy_lab1'),
            subscribedCapacityRatioLab: i18nService.get('storage.storagePool_view_task_Total_Subscribed_CapacityRation_label'),
            subscribedCapacityRatioTip: i18nService.get('storage.storagePool_view_task_Total_Subscribed_CapacityRation_tip'),
            mappingCapacityLab: i18nService.get('storage.storagePool_view_task_mappingCap_lab'),
            mappingCapacityTip: i18nService.get('storage.storagePool_view_task_mappingCap_tip'),
            shareCapacityLab: i18nService.get('storage.storagePool_view_task_shareCap_lab'),
            shareCapacityTip: i18nService.get('storage.storagePool_view_task_shareCap_tip'),
            maxLab: i18nService.get('storage.storagePool_view_task_summary_subscribedCapacity_lab'),
            usedTotalLab: i18nService.get('common.usedandTotal_tip'),
            protectedLogicalCapacityLab: i18nService.get('storage.dashboard_term_poolDataProtectionLogicalCapacity_lab'),
        };
    }

    get expandI18n(): { [key: string]: string | any } {
        const i18nService = this.i18nService;
        return {
            ...this.commonI18n,
            winTitle: i18nService.get('storage.storagePool_expand_task_title_wintitle'),

            noExpandDiskTip: i18nService.get('storage.storagePool_expand_msg'),
            currentCapacityLab: i18nService.get('common.currentCapacity_label'),
            expandedCapacityLab: i18nService.get('common.expansionCapacity_label'),
            allDiskLab: i18nService.get('storage.diskPool_term_diskType_value_allDisk_value'),

            confirmBtn: i18nService.get('common.confirm_button'),
            cancelBtn: i18nService.get('common.cancel_button'),

            expandMsg: i18nService.get('storage.storagePool_task_expand_confirmDisk_msg'),
            expandPerformLayerTitle: i18nService.get('storage.storagePool_term_expand_performLayer_title'),

            taskCreationResult: i18nService.get('storage.storagePool_task_result_value1'),
            queryProgressResult: i18nService.get('common.task_getProgress_lab'),
            queryProgressTimeoutTip: i18nService.get('common.task_progressTimeout_tip'),
            expandPerformLayerMsg: (name: string) => i18nService.get('rest.expandPerformLayer_msg', [name]),
            selectEnclosureNumInfo: this.get('storage.storagePool_task_create_selectCapacityControlNum_info'),
        };
    }

    get modifyI18n(): { [key: string]: string | any } {
        const i18nService = this.i18nService;
        return {
            ...this.commonI18n,
            winTitle: i18nService.get('storage.storagePool_modify_task_title_wintitle'),
            nameLab: i18nService.get('common.name_column'),
            descriptionLab: i18nService.get('common.description_column'),
            alarmThresholdLab: i18nService.get('storage.storagePool_create_task_para_consumedCapThreshold_label'),
            raidLevelLab: i18nService.get('storage.storagePool_term_raidStrategy_label'),
            // eslint-disable-next-line max-len
            usedUpAlarmThresholdLab: i18nService.get('storage.storgePool_create_task_seniorsetting_para_tobe_exhausted_capacity_percent_label'),
            usedUpAlarmThresholdLabTip: i18nService.get('storage.storgePool_modify_task_capacityUsedUp_tip'),
            enableThinLunOverLab: i18nService.get('storage.storgePool_create_task_seniorsetting_para_thinLUNProportion_label'),
            overPercentageLab: i18nService.get('storage.storgePool_create_task_seniorsetting_para_thinLUNProportion_percent_label'),
            autoDeleteSwitchLab: i18nService.get('storage.storagePool_create_task_para_autoDelete_noUnit_label'),
            protectionCapacityLowLimitLab: i18nService.get('storage.storagePool_create_task_para_protectCapacityLowThreshold_label'),
            protectionCapacityHighLimitLab: i18nService.get('storage.storagePool_create_task_para_protectCapacitUpThresholdy_label'),

            consumedCapacityThresholdCompareNumErr: i18nService.get('storage.storgePool_create_task_cpaAlarmTeshold_value_tip'),
            protectCapacityThresholdCompareNumErr: i18nService.get('storage.storagePool_create_task_protectCapacity_compare_tip'),

            openAutoDeleteTitle: i18nService.get('storage.storagePool_modify_task_openAutoDelete_title'),
            closeAutoDeleteTitle: i18nService.get('storage.storagePool_modify_task_closeAutoDelete_title'),
            openAutoDeleteMsg: i18nService.get('storage.storagePool_modify_task_openAutoDelete_msg'),
            closeAutoDeleteMsg: i18nService.get('storage.storagePool_modify_task_closeAutoDelete_msg'),

            confirmBtn: i18nService.get('common.confirm_button'),
            cancelBtn: i18nService.get('common.cancel_button'),

            succeedTip: i18nService.get('common.rest_success_label', [i18nService.get('rest.modifyStoragePool_msg')]),

            modifyStoragePoolRest: i18nService.get('rest.modifyStoragePool_msg'),
            modifyHotDiskRest: i18nService.get('rest.modify_hotSpareDiskNum_msg'),
            modifyDiskPool: i18nService.get('rest.modifyDiskPool_msg'),
            quotaModifyTitle: i18nService.get('service.quota_term_quotaMana_modify_title'),
            storagePoolConfirmationMessage: i18nService.get('storage.storagePool_change_task_confirmation_message'),
            quotaValid: i18nService.get('storage.storagePool_view_task_quota_valid'),
            defaultLabel: this.i18nService.get('common.default_label'),
        };
    }

    get poolI18n(): { [key: string]: string | any } {
        const i18nService = this.i18nService;
        return {
            ...this.commonI18n,
            createBtn: i18nService.get('common.create_button'),
            expandBtn: i18nService.get('common.expand_label'),
            rekeyBtn: i18nService.get('storage.storagePool_updatekey_button'),
            modifyBtn: i18nService.get('common.modify_button'),
            deleteBtn: i18nService.get('common.delete_button'),

            nameLab: i18nService.get('common.name_column'),
            operationLab: i18nService.get('common.operation_button'),
            enableLab: i18nService.get('monitor.alarm_term_smartFix_suggest_enableNow_btn'),
            healthStatusLab: i18nService.get('common.healthStatus_column'),
            runningStatusLab: i18nService.get('common.runningStatus_column'),
            totalCapacityLab: i18nService.get('common.totalCapacity_lab'),
            usedLab: i18nService.get('storage.dashboard_term_CapacityInfo_UsedRawCapacity_label'),
            unusedLab: i18nService.get('storage.dashboard_term_CapacityInfo_UnusedRawCapacity_label'),
            dataProtectionCapacityLab: i18nService.get('storage.storagePool_term_dataProtection_column'),
            dataReductionRatioLab: i18nService.get('storage.dashboad_term_dataCompression_label'),
            thinLunSpaceSavingRateLab: i18nService.get('storage.storgePool_view_term_thinLUN_spacePercentage_label'),
            overallSpaceSavingRatioLab: i18nService.get('storage.storgePool_view_term_whole_spacePercentage_label'),
            subscripedCapacityLab: i18nService.get('storage.dashboard_term_Total_subscribed_lab'),
            usedSubscripedCapacityLab: i18nService.get('storage.dashboard_term_usedSubscribed_lab'),
            unusedSubscripedCapacityLab: i18nService.get('storage.dashboard_term_unusedSubscribed_lab'),
            reclaimSubscribedCapacityLab: i18nService.get('storage.dashboard_term_recyclingSubscribedCapacity_lab'),
            reclaimLab: i18nService.get('storage.dashboard_term_recycling_lab'),
            protectedLogicalCapacityLab: i18nService.get('storage.dashboard_term_poolDataProtectionLogicalCapacity_lab'),

            alarmThresholdLab: i18nService.get('storage.storagePool_create_task_para_consumedCapThreshold_lab1'),
            usedUpAlarmThresholdLab: i18nService.get('storage.storagePool_create_task_tobe_exhausted_capacity_lab'),
            notEncryptDiskTip: i18nService.get('storage.storagePool_updatekey_tip'),
            capNotZeroTip: i18nService.get('storage.storagePool_delete_tip'),
            noDiskTip: i18nService.get('storage.storagePool_expand_task_noValidController_msg'),
            noExpandDiskTip: i18nService.get('storage.storagePool_expand_msg'),
            taskCreationResult: i18nService.get('storage.storagePool_task_result_value1'),
            queryProgressResult: i18nService.get('common.task_getProgress_lab'),
            operationSuccTip: i18nService.get('operationSucc_lab'),
            queryProgressTimeoutTip: i18nService.get('common.task_progressTimeout_tip'),

            rekeyMsg: i18nService.get('storage.storagePool_task_updateKey_tip'),

            startLtEnd: i18nService.get('storage.storagePool_task_recycleIDTable_startTime_valid'),
            endGtStart: i18nService.get('storage.storagePool_task_recycleIDTable_endTime_valid'),
            valueEqTip: i18nService.get('storage.storagePool_task_recycleIDTable_endTimeEqual_valid'),
            performLayerErrTip: i18nService.get('storage.storagePool_term_expand_performLayer_err_tip'),
            exclPerformLayerErrTip: i18nService.get('storage.storagePool_term_expand_exclPerformLayer_err_tip'),
        };
    }

    constructor(
        private i18nService: I18nService,
        private utilService: UtilService,
        private toolService: ToolService,
    ) { }

    getAvailableCapMsg(mode: 'create' | 'expand' | 'flashExpand'): string {
        if (mode === 'create') {
            return this.get('rest.createStoragePool_msg');
        } else if (mode === 'expand') {
            return this.get('rest.expandStoragePool_msg');
        } else {
            return '';
        }
    }

    getEnclosureDisks(diskNum: number | string): string {
        return this.get('common.colon_label') + this.get('storage.lun_task_expand_diskNum_label', [diskNum]);
    }

    getDiskNumMsg(engineNum: number, totalNum: number, smartTier: boolean, engineInfo: EngineInfo): string {
        // 微存储不体现控制框
        if (FeatureConfig.IsReplaceStrLunWithNamespace) {
            return this.get('storage.storagePool_task_expand_number_needDisk_selectController_tip', [totalNum]);
        }

        // 判断是否是内双活引擎
        const enclosureNum = this.getEnclosureNum(engineInfo);
        const isAA = enclosureNum > 1;
        if (smartTier && isAA) {
            return this.get('storage.storagePool_task_create_enableSmartTier_selectControlerDisk_info', [enclosureNum, totalNum]);
        }
        if (smartTier && !isAA) {
            return this.get('storage.storagePool_task_create_enableSmartTier_selectControlerDisk_info', [engineNum, totalNum]);
        }
        if (!smartTier && isAA) {
            return this.get('storage.storagePool_task_expand_number_needDisk_selectController_tip', [enclosureNum, totalNum]);
        }
        if (!smartTier && !isAA) {
            return this.get('storage.storagePool_task_expand_number_needDisk_selectController_tip', [engineNum, totalNum]);
        }
        return '';
    }

    getPerformDiskNumMsg(engineNum: number, totalNum: number, engineInfo: EngineInfo): string {
        const enclosureNum = this.getEnclosureNum(engineInfo);
        let performTierNeedDiskTip = 'storage.storagePool_task_create_number_performTierNeedDisk_selectController_tip';

        //不支持容量层时用不体现容量层的提示
        if (!FeatureConfig.IsSupportCapacityLayer) {
            performTierNeedDiskTip = 'storage.storagePool_task_create_number_performTierNeedDisk_selectController_tip2';
        }

        if (enclosureNum > 1) {
            return this.get(performTierNeedDiskTip, [enclosureNum, totalNum]);
        }

        return this.get(performTierNeedDiskTip, [engineNum, totalNum]);
    }

    getAddCapStr(capStr: string): string {
        return this.get('storage.lun_task_expand_capacity_label') + this.get('common.colon_label') + capStr;
    }

    getBracketMsg(str: string): string {
        return this.get('common.brackets_connection_lab', [str]);
    }

    getModifyPerformLayerQuotaMsg(str: string): string {
        return this.get('rest.modifyPerformLayerQuota_msg', [str]);
    }

    /**
     * 是否显示容量偏差提示，不足一个sector
     */
    getLossTip(cap: Capacity): string {
        let capLossTip: string;
        const { value, unit } = cap;

        if (value && (unit !== Consts.SECTOR && unit !== Consts.MB)) {
            const capByteObj = this.utilService.adaptUnit(value, unit, Consts.SECTOR);
            if (Math.floor(capByteObj.value) !== capByteObj.value) {
                capLossTip = this.get('storage.storagePool_view_task_modifyQuota_desc',
                    [value + ' ' + unit, capByteObj.value.toFixed(3)]);
            }
        }

        return capLossTip;
    }

    /**
     * 将内容用括号包起来
     */
    bracket = (content: string): string => this.get('common.common_unit_lab', [content]);

    // 平均寿命
    getRemainLife(days: number, unit = true): string {
        if (!days || Number(days) <= 0) {
            return UNPLACEHOLDER;
        }
        const months = Math.floor(days / 30);
        let result;
        if (months < 1) {
            result = '< 1';
        } else if (months > 60) {
            result = '> 60';
        } else {
            result = months;
        }
        return unit ? result + ' ' + this.i18n.monthLab : result;
    }

    getDeleteMsg(name: string): void {
        this.get('storage.storagePool_task_deletePool_FS_confirm_msg', [name]);
    }

    getExportInfo(name: string): string {
        return `${this.get('common.name_column')}${this.get('common.colon_label')}${name}`;
    }

    /**
     * 已使用容量及百分比渲染
     */
    usedCapacityRender = (usedCapacity: string | number, usedPercent: string | number, keepPercentRaw = false): string => {
        const capacity = this.utilService.adaptUnit(usedCapacity, CAPACITY_UNIT.SECTOR);
        const percent = keepPercentRaw ? `${usedPercent}%` : this.utilService.percent(Number(usedPercent));
        return this.get('common.used_tip', [`${capacity.label} (${percent})`]);
    };

    /**
     * smartpool已使用容量及百分比渲染
     */
    smartpoolCapacityRender = (usedCapacity: string | number, usedPercent: string | number, keepPercentRaw = false, isCache: boolean): string => {
        const usedPercentValue = isNaN(Number(usedPercent)) ? 0 : usedPercent;
        const usedCapacityValue = isNaN(Number(usedCapacity)) ? 0 : usedCapacity;
        const capacity = this.utilService.adaptUnit(usedCapacityValue, CAPACITY_UNIT.SECTOR);
        const percent = keepPercentRaw ? `${usedPercentValue}%` : this.utilService.percent(Number(usedPercent));
        const str = isCache ?
            this.get('storage.storagePool_view_task_cacheUsedCap_lab') :
            this.get('storage.storagePool_view_task_dataUsedCap_lab');

        return `${capacity.label} (${percent})` + str;
    };

    /**
     * 已使用订阅容量及百分比渲染
     */
    usedSubscripedCapacityRender = (usedCapacity: number, totalCapacity: number): string => {
        const capacity = this.utilService.adaptUnit(usedCapacity, CAPACITY_UNIT.SECTOR);
        const percent = this.utilService.percent(totalCapacity !== 0 ? usedCapacity / totalCapacity : 0);
        return this.get('common.used_tip', [`${capacity.label} (${percent})`]);
    };

    /**
     * 未使用容量渲染
     */
    freeCapacityRender = (freeCapacity: string | number): string => {
        const capacity = Math.max(0, Number(freeCapacity));
        return this.get('common.unused_tip', [this.utilService.adaptUnit(capacity, CAPACITY_UNIT.SECTOR).label]);
    };

    /**
     * 性能层已使用容量渲染
     */
    flashlayerCapacityRender = (flashlayerCapacity: string | number): string => {
        const capacity = Math.max(0, Number(flashlayerCapacity));
        return this.get('common.used_tip', [this.utilService.adaptUnit(capacity, CAPACITY_UNIT.SECTOR).label]);
    };

    /**
     * 热备容量渲染
     */
    spareCapacityRender = (spareCapacity: string | number): string => {
        const capacity = Math.max(0, Number(spareCapacity));
        return `${this.utilService.adaptUnit(capacity, CAPACITY_UNIT.SECTOR).label}${this.isEn ? ' ' : ''}${this.get('storage.storagePool_view_task_hotSpare_lab')}`;
    };

    /**
     * 热备已使用容量及百分比渲染
     */
    spareUsedCapacityRender = (usedCapacity: string | number, totalCapacity: string | number): string => {
        if (this.toolService.isInvalidNum(String(totalCapacity), true)) {
            return this.get('common.used_tip', [UNPLACEHOLDER]);
        }

        const percent = Number(totalCapacity) && totalCapacity !== '18446744073709551615' ?
            Number(usedCapacity) / Number(totalCapacity) :
            0;

        return this.usedCapacityRender(usedCapacity, percent);
    };

    /**
     * 热备未使用容量渲染
     */
    spareFreeCapacityRender = (usedCapacity: string | number, totalCapacity: string | number): string => {
        const freeCapacity = !this.toolService.isInvalidNum(String(totalCapacity), true) &&
            !this.toolService.isInvalidNum(String(usedCapacity), true) ? Number(totalCapacity) - Number(usedCapacity) : NaN;

        return this.freeCapacityRender(freeCapacity);
    };

    /**
     * get_engine 和 fromdev 返回的diskType格式不一样
     * 有的包含了"-"、"_"，全部替换成空格
     */
    convertDiskType(oriDiskType: string): string {
        if (this.showDiskTypeByI18nDisks[oriDiskType]) {
            return this.showDiskTypeByI18nDisks[oriDiskType];
        }

        let diskType = oriDiskType.replace(/-/g, ' ').replace(/_/g, ' ');

        if (diskType === 'SCM SSD') {
            diskType = 'SCM';
        }

        for (const opt of this.diskTypeOpts) {
            if (opt.label.toUpperCase() === diskType.toUpperCase()) {
                return opt.label;
            }
        }

        return diskType;
    }

    getDiskTypeKey(diskType: string): DiskTypeE {
        const diskTypeNew = this.convertDiskType(diskType);

        //真假容量型硬盘的lable相同，特殊适配返回
        if (DiskTypeAndEnumMap.get(diskType)) {
            return DiskTypeAndEnumMap.get(diskType);
        }

        for (const opt of this.diskTypeOpts) {
            const label = opt.label.replace(/-/g, ' ').replace(/_/g, ' ');
            if (label === diskTypeNew) {
                return opt.value;
            }
        }
        return null;
    }

    isEncryptDisk(diskType: string): boolean {
        const diskTypeNew = this.convertDiskType(diskType);
        const diskTypeValue = this.getDiskTypeKey(diskTypeNew);
        return EncryptDisks.includes(diskTypeValue);
    }

    parseEngineData(engine: string): EngineData {
        const exg = /EngineId:(\d+);Enclosure:(.+)/;
        const exgReturn = exg.exec(engine);
        const engineId = exgReturn[1];
        const engineLabel = exgReturn[2];
        return {
            engine: engineLabel,
            engineId,
        };
    }

    convertEngine(engineInfo: any[]): { engineArr: EngineData[]; diskArr: CapacityDisk[] } {
        const engineArr: EngineData[] = [];
        let diskArr = [];

        if (!engineInfo || !engineInfo.length) {
            return {
                engineArr,
                diskArr,
            };
        }

        for (const engineInfoItem of engineInfo) {
            const key = Object.keys(engineInfoItem)[0];
            const engineDisk = engineInfoItem[key];

            const engineData: EngineData = this.parseEngineData(key);
            let diskNum = 0;
            for (const disk of engineDisk) {
                diskNum += Number(disk.diskNum);
            }
            engineData.diskNum = diskNum;
            const diskType = engineDisk[0] ? engineDisk[0].diskType : '';
            engineData.diskType = this.convertDiskType(diskType);
            engineData.diskData = engineInfoItem[key];
            diskArr = diskArr.concat(engineData.diskData);
            engineArr.push(engineData);
        }

        return {
            engineArr,
            diskArr,
        };
    }

    selectedEngineInfo(engine: string, enclosureCount: number): string {
        return this.get('storage.storagePool_task_create_selectedEncloDetail_lab', [engine, enclosureCount]);
    }

    selectedDiskEnclosureInfo(diskEnclosure: string, diskCount: number): string {
        return this.get('storage.storagePool_task_create_selectedDiskDetail_lab', [diskEnclosure, diskCount]);
    }

    enclosureRedundancyMinDiskErr(enclosureCount: number): string {
        return this.get('storage.storagePool_task_create_requiredDisk_error_info_lab', [enclosureCount]);
    }

    selectDisksInfo(enclosureCount: number): string {
        return this.get('storage.storagePool_task_create_selectDisks_info', [enclosureCount]);
    }

    selectDiskEnclosureTip = (): string => this.get('storage.storagePool_task_create_selectEnclo_info_lab', [Constants.EachEngineDiskEnclosureMin]);

    selectDiskEnclosureErr(): string {
        return this.get('storage.storagePool_task_create_selectEnclo_error_info_lab', [Constants.EachEngineDiskEnclosureMin]);
    }

    getNextDayMsg(name: string): string {
        return this.get('storage.storagePool_task_recycleIDTable_nextDay_info', [name]);
    }

    /**
     * 获取内双活引擎控制框数量
     */
    getEnclosureNum(engineInfo: EngineInfo): number {
        const freeEengineArr = this.convertEngine(engineInfo.freeDisk).engineArr;
        const enclosureNum = freeEengineArr[0].engine.split(',').length;

        return enclosureNum;
    }

    /**
     * 根据不同业务场景，获取容量热备策略校验错误提示
     * @param specialCount 传入则返回对应数量的Tip
     */
    getHddHotSpareTips(specialCount?: number): string {
        if (specialCount) {
            return this.i18n.capHotSpareDiskNumCommonTip(specialCount);
        }

        if (FeatureConfig.MinNotHotSpareDiskNumOfEachEngine === 4) {
            return this.i18n.capacityTierRequirDiskInfo;
        }

        if (SWITCH.SUPPORT_X_DISK_CREATE_POOL) {
            return this.i18n.capHotSpareDiskNumCommonTip(Constants.EachHotSpareEngineDiskMin());
        }

        return this.i18n.capHotSpareDiskNumTip;
    }

    /**
     * 处理AP模式的高危提示
     * @param params 要下发的创建存储池参数
     * @param prompts 高危提示数组
     */
    handleWorkModePrompt(params, prompts: PromptConfigFull[]): void {
        if (params.dataAffinityMode === StoragePoolAndLunWorkModeE.AP_MODE) {
            prompts.push({
                id: 'prompt-pool-create-ap-mode',
                opType: 'info',
                title: this.createI18n.winTitle,
                content: this.createI18n.createApPoolTip,
            });
        }
    }
}
